import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';

/**
 * Qué acto del Recorrido se está mirando.
 *
 * Existe por una razón puntual: desde que el Recorrido es UNA página que se scrollea (card
 * LDdcpf52), el paso activo ya no se puede leer de la URL. Los cuatro actos comparten el mismo
 * path y el hash sólo dice por dónde se ENTRÓ, no dónde está el usuario tres scrolls después.
 * Quien lo sabe es el observer de secciones del contenedor, y quien necesita saberlo es el grupo
 * del sidebar — que es hermano del contenido, no su hijo.
 *
 * Vive en `App.js`, con el provider de módulos y el de trabajo pendiente, porque es lo único que
 * no remonta al navegar. Colgarlo más abajo lo dejaría del lado del contenido, que es justo el que
 * no puede alcanzar al sidebar.
 *
 * Es deliberadamente chico: el estado del Recorrido (hechos, documentos, contadores) NO va acá.
 * Esto es sólo "qué sección se ve", que es lo único que dos ramas separadas del árbol comparten.
 */

const BoosterFlowContext = createContext({
    activeSection: null,
    setActiveSection: () => {},
});

export function BoosterFlowProvider({children}) {
    const [activeSection, setActiveSectionState] = useState(null);

    // Idempotente a propósito: el observer puede reportar la misma sección varias veces mientras
    // se scrollea dentro de un acto largo, y un `setState` por evento de scroll re-renderizaría el
    // sidebar decenas de veces por segundo sin que nada haya cambiado.
    const setActiveSection = useCallback((section) => {
        setActiveSectionState(prev => (prev === section ? prev : section || null));
    }, []);

    const value = useMemo(() => ({activeSection, setActiveSection}), [activeSection, setActiveSection]);

    return (
        <BoosterFlowContext.Provider value={value}>
            {children}
        </BoosterFlowContext.Provider>
    );
}

export const useBoosterFlow = () => useContext(BoosterFlowContext);

export default BoosterFlowContext;
